import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import type { ResourceMapRelationType } from "@k8s-dashboard/contracts";
import { useResourceCatalog, useResourceMap } from "@/api/queries";
import { useDashboardParams } from "@/state/useDashboardParams";
import { EMPTY_FILTERS, MAX_DEPTH, RELATION_ORDER, parseFocus } from "./resource-map-focus";
import { StateBlock, errorNotice } from "./ResourceMapNotices";
import { ResourceMapPanels } from "./ResourceMapPanels";
import type { ResourceMapGraphEdge, ResourceMapGraphNode } from "./ResourceMapGraph";

/**
 * Resource Map (ADR 0020, #8)
 * --------------------------------------------------------------------------
 * Explorer에서 고른 focus(res=group/version/resource, item=namespace/name/uid)의
 * **조회 전용** 관계 그래프의 컨트롤러입니다.
 *
 * - 요청은 focus·depth·관계 필터당 한 번입니다. 폴링하지 않고, 갱신은 "다시 조회"뿐입니다.
 * - URL은 focus의 위치일 뿐입니다. 적용 Scope는 **서버 응답의 appliedScope**만
 *   보여줍니다 — URL 값으로 Scope를 만들어 보여주지 않습니다.
 * - 서버 배열 순서를 그대로 보존합니다 — 정본인 노드·간선 표는 재정렬하지 않습니다.
 *   유계 시각 미리보기만 **결정적 클라이언트 배치**(힘-기반·난수 아님)를 씁니다.
 * - 값이 있는 필드(라벨 값·data·env·원본 객체)는 응답에 없고, 여기서도 만들지
 *   않습니다. Evidence.field는 경로/키 이름이라 표시합니다.
 * - 생성·수정·삭제·적용 컨트롤이 없습니다. depth·관계 필터 선택·시각 미리보기의
 *   focus 이동과 명시적 재조회뿐입니다.
 *
 * URL 파싱은 resource-map-focus.ts, 고정 문구는 ResourceMapNotices.tsx, 성공 응답
 * 패널(시각 미리보기 + 정본 표)은 ResourceMapPanels.tsx가 소유합니다.
 */
export function ResourceMapView() {
  const [params, setParams] = useSearchParams();
  const { clusterId } = useDashboardParams();
  const parsed = parseFocus(params, clusterId);

  const filters = parsed.kind === "ok" ? parsed.filters : EMPTY_FILTERS;
  const query = useResourceMap(filters, parsed.kind === "ok");
  /* 시각 미리보기의 노드 선택은 URL focus(res=group/version/resource)를 바꿔야
     하는데 응답 노드에는 resource(복수형)가 없습니다. 이미 캐시된 카탈로그
     (Explorer와 같은 안정 키)에서 group/version/kind → resource를 해석합니다.
     매핑이 없는 노드는 fail-closed로 선택 불가입니다. */
  const catalog = useResourceCatalog(clusterId, parsed.kind === "ok");

  const data = query.data;
  const resourceOf = useMemo(() => {
    const table = new Map<string, string>();
    for (const d of catalog.data?.items ?? []) table.set(`${d.group}/${d.version}/${d.kind}`, d.resource);
    return table;
  }, [catalog.data]);
  /* 시각 미리보기 입력 — 서버 순서 그대로, 값 필드 없이 신원·상태만 옮깁니다. */
  const graphNodes = useMemo<ResourceMapGraphNode[]>(() => {
    if (!data) return [];
    return data.nodes.map((node) => ({
      key: node.key,
      kind: node.kind,
      namespace: node.namespace ?? "",
      name: node.name,
      uid: node.uid ?? "",
      resolved: node.resolved,
      health: node.health,
      focus: node.key === data.focus.key,
      resource: resourceOf.get(`${node.group}/${node.version}/${node.kind}`) ?? "",
    }));
  }, [data, resourceOf]);
  const graphEdges = useMemo<ResourceMapGraphEdge[]>(
    () => (data ? data.edges.map((e) => ({ from: e.from, to: e.to, type: e.type, resolved: e.resolved })) : []),
    [data],
  );
  /* 노드 선택은 기존 URL focus만 바꿉니다(replace) — cluster·검색·Scope·depth·
     relation 등 무관한 질의는 그대로이고 tab=map은 canonical로 고정됩니다. 새 focus의
     정상 질의 한 번이 그 결과입니다. */
  const selectGraphNode = (node: ResourceMapGraphNode) => {
    const target = data?.nodes.find((n) => n.key === node.key);
    if (!target || !node.resolved || !node.uid || !node.resource) return;
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set("res", `${target.group}/${target.version}/${node.resource}`);
        p.set("item", `${node.namespace}/${node.name}/${node.uid}`);
        p.set("tab", "map");
        return p;
      },
      { replace: true },
    );
  };

  const setDepth = (value: string) =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set("depth", value);
        return p;
      },
      { replace: true },
    );

  /* 관계 체크박스 토글 — 무관한 질의 상태는 건드리지 않습니다. 전체 선택은 relation
     파라미터 부재(canonical all)로, 부분집합은 canonical 순서의 반복 값으로 씁니다.
     최소 한 개는 남습니다 — 0개 선택은 "아무것도 조회하지 않음"이라 만들지 않습니다. */
  const toggleRelation = (relation: ResourceMapRelationType) => {
    if (parsed.kind !== "ok") return;
    const next = new Set(parsed.selected);
    if (next.has(relation)) {
      if (next.size === 1) return;
      next.delete(relation);
    } else {
      next.add(relation);
    }
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.delete("relation");
        if (next.size < RELATION_ORDER.length) {
          for (const value of RELATION_ORDER) {
            if (next.has(value)) p.append("relation", value);
          }
        }
        return p;
      },
      { replace: true },
    );
  };

  if (parsed.kind === "empty") {
    return (
      <section className="panel">
        <div className="panel__body">
          <StateBlock
            role="status"
            notice={{
              title: "Explorer에서 항목을 선택하세요",
              detail: "Resource Map은 Explorer에서 고른 리소스 하나를 focus로 관계 그래프를 그립니다.",
              tone: "neutral",
            }}
          />
        </div>
      </section>
    );
  }

  if (parsed.kind === "invalid") {
    /* 잘못된 focus/depth/relation은 요청 0회로 여기서 끝납니다. */
    return (
      <section className="panel">
        <div className="panel__body">
          <StateBlock role="alert" notice={{ title: "요청을 보내지 않았습니다", detail: parsed.reason, tone: "error" }} />
        </div>
      </section>
    );
  }

  const focusTitle = `${filters.group}/${filters.version}/${filters.resource} · ${
    filters.namespace ? `${filters.namespace}/` : ""
  }${filters.name}`;

  return (
    <section className="panel">
      <div className="panel__header">
        <div>
          <h2 className="panel__title">Resource Map</h2>
          <div className="panel__subtitle">{focusTitle}</div>
        </div>
        <div className="panel__actions">
          <label className="field">
            <span className="field__label">깊이 (1–{MAX_DEPTH})</span>
            <select value={String(parsed.requestedDepth)} onChange={(e) => setDepth(e.target.value)} aria-label="순회 깊이">
              {[1, 2, 3, 4].map((d) => (
                <option key={d} value={String(d)}>
                  {d}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="chip"
            onClick={() => void query.refetch()}
            disabled={query.isFetching}
            aria-busy={query.isFetching}
          >
            {query.isFetching ? "조회 중…" : "다시 조회"}
          </button>
        </div>
      </div>
      <div className="panel__body">
        <fieldset className="chips" aria-label="관계 필터">
          <legend className="field__label">관계 필터 (좁히기 전용)</legend>
          {RELATION_ORDER.map((relation) => (
            <label key={relation} className="chip">
              <input
                type="checkbox"
                checked={parsed.selected.has(relation)}
                onChange={() => toggleRelation(relation)}
              />{" "}
              {relation}
            </label>
          ))}
        </fieldset>

        {query.error && !data ? (
          <StateBlock notice={errorNotice(query.error)} />
        ) : !data ? (
          <div className="state" role="status">
            <span className="state__glyph" aria-hidden="true">
              …
            </span>
            <span className="state__title">관계 그래프를 불러오는 중입니다</span>
          </div>
        ) : (
          <ResourceMapPanels
            data={data}
            requestedDepth={parsed.requestedDepth}
            refetchFailed={Boolean(query.error)}
            focusTitle={focusTitle}
            graphNodes={graphNodes}
            graphEdges={graphEdges}
            onSelectNode={selectGraphNode}
          />
        )}
      </div>
    </section>
  );
}
